import { ReactNode } from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface DataTableCardProps {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  minHeight?: number;
}

export default function DataTableCard({ title, subtitle, actions, children, minHeight = 420 }: DataTableCardProps) {
  const { t } = useI18n();
  const resolvedTitle = title ? translateUiText(title, t) : undefined;
  const resolvedSubtitle = subtitle ? translateUiText(subtitle, t) : undefined;
  const resolvedMinHeight = typeof minHeight === 'number'
    ? { xs: Math.min(minHeight, 360), sm: minHeight }
    : minHeight;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      {(resolvedTitle || actions) && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box minWidth={0}>
            {resolvedTitle && (
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 650, letterSpacing: '-0.02em', lineHeight: 1.3 }}
              >
                {resolvedTitle}
              </Typography>
            )}
            {resolvedSubtitle && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {resolvedSubtitle}
              </Typography>
            )}
          </Box>
          {actions}
        </Box>
      )}
      <Box sx={{ p: 0, minHeight: resolvedMinHeight }}>
        {children}
      </Box>
    </Paper>
  );
}
